import { Check, Search, X } from 'lucide-react';
import * as React from 'react';
import { StatusBuildBadge } from '@/components/patterns/status-badge';
import { Input, Label } from '@/components/ui/field';
import { Skeleton } from '@/components/ui/skeleton';
import { dataSource } from '@/data';
import type { BuildResumo } from '@/data/types';
import { cn } from '@/lib/utils';

const ATRASO_MS = 250;

/**
 * Seletor de versões com busca: lista as versões do feed (mais recentes
 * primeiro; digitar filtra no servidor pelo trecho da versão) e acumula as
 * escolhidas em chips removíveis. Versões em `bloqueadas` aparecem
 * desabilitadas com o motivo (ex.: já associada ao incidente).
 */
export function VersoesSeletor({
  id,
  rotulo = 'Versões afetadas',
  obrigatorio = false,
  valores,
  aoMudar,
  produto,
  bloqueadas = [],
  motivoBloqueio = 'Já associada',
  erro,
}: {
  id: string;
  rotulo?: string;
  obrigatorio?: boolean;
  valores: string[];
  aoMudar: (valores: string[]) => void;
  produto?: string;
  bloqueadas?: string[];
  motivoBloqueio?: string;
  erro?: string | null;
}) {
  const [busca, setBusca] = React.useState('');
  const [termo, setTermo] = React.useState('');
  const [resultados, setResultados] = React.useState<BuildResumo[]>([]);
  const [estado, setEstado] = React.useState<'carregando' | 'erro' | 'pronto'>('carregando');
  const [tentativa, setTentativa] = React.useState(0);

  React.useEffect(() => {
    const idTempo = window.setTimeout(() => setTermo(busca.trim()), ATRASO_MS);
    return () => window.clearTimeout(idTempo);
  }, [busca]);

  React.useEffect(() => {
    const controller = new AbortController();
    setEstado('carregando');
    dataSource
      .listarBuilds({ produto, q: termo || null, limit: 40 }, null, controller.signal)
      .then((pagina) => {
        setResultados(pagina.items);
        setEstado('pronto');
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return;
        setEstado('erro');
      });
    return () => controller.abort();
  }, [termo, produto, tentativa]);

  function alternar(versao: string) {
    aoMudar(valores.includes(versao) ? valores.filter((item) => item !== versao) : [...valores, versao]);
  }

  const listaId = `${id}-lista`;
  const erroId = erro ? `${id}-erro` : undefined;

  return (
    <div className="flex flex-col gap-2" role="group" aria-labelledby={`${id}-rotulo`}>
      <Label id={`${id}-rotulo`} htmlFor={id}>
        {rotulo}
        {obrigatorio ? <span className="ml-1 text-danger">*</span> : null}
      </Label>

      {valores.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5" aria-label="Versões selecionadas">
          {valores.map((versao) => (
            <li key={versao}>
              <span className="inline-flex items-center gap-1 rounded-chip border border-primary-border bg-primary-wash py-0.5 pl-2 pr-0.5 font-mono text-label text-primary">
                {versao}
                <button
                  type="button"
                  onClick={() => alternar(versao)}
                  aria-label={`Remover a versão ${versao}`}
                  className="inline-flex size-5 items-center justify-center rounded-chip hover:bg-primary-wash-strong"
                >
                  <X className="size-3" aria-hidden="true" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-icon" aria-hidden="true" />
        <Input
          id={id}
          type="search"
          value={busca}
          onChange={(evento) => setBusca(evento.target.value)}
          placeholder="Buscar versão, ex.: 2.2602.78"
          aria-describedby={erroId}
          aria-invalid={erro ? true : undefined}
          aria-controls={listaId}
          className="pl-9"
        />
      </div>

      <div className="cmp-scroll-area max-h-52 overflow-y-auto rounded-input border border-border">
        {estado === 'carregando' && resultados.length === 0 ? (
          <div className="flex flex-col gap-1.5 p-2" role="status" aria-label="Carregando versões">
            {Array.from({ length: 4 }).map((_, indice) => (
              <Skeleton key={indice} className="h-8" />
            ))}
          </div>
        ) : estado === 'erro' ? (
          <p role="alert" className="flex flex-wrap items-center gap-2 px-3 py-4 text-body text-danger">
            Não foi possível carregar as versões.
            <button type="button" className="font-bold underline underline-offset-4" onClick={() => setTentativa((v) => v + 1)}>
              Tentar de novo
            </button>
          </p>
        ) : resultados.length === 0 ? (
          <p className="px-3 py-4 text-center text-body text-on-surface-muted">Nenhuma versão encontrada para esta busca.</p>
        ) : (
          <ul id={listaId} role="listbox" aria-multiselectable="true" aria-label={rotulo} className="p-1">
            {resultados.map((build) => {
              const escolhida = valores.includes(build.versao);
              const bloqueada = bloqueadas.includes(build.versao);
              return (
                <li key={build.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={escolhida}
                    disabled={bloqueada}
                    title={bloqueada ? motivoBloqueio : undefined}
                    onClick={() => alternar(build.versao)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-chip px-2 py-1.5 text-left text-body transition-colors duration-fast ease-standard',
                      bloqueada ? 'cursor-not-allowed text-on-surface-muted' : 'hover:bg-bg-subtle',
                      escolhida && 'bg-primary-wash',
                    )}
                  >
                    <Check className={cn('size-4 shrink-0 text-primary', !escolhida && 'invisible')} aria-hidden="true" />
                    <span className="font-mono">{build.versao}</span>
                    <span className="hidden truncate text-cell text-on-surface-muted sm:inline">{build.branch}</span>
                    <span className="ml-auto flex shrink-0 items-center gap-2">
                      {bloqueada ? <span className="text-label font-normal text-on-surface-muted">{motivoBloqueio}</span> : null}
                      <StatusBuildBadge status={build.status} />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {erro ? (
        <p id={erroId} role="alert" className="text-label text-danger">
          {erro}
        </p>
      ) : null}
    </div>
  );
}
